import React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { FileText, Sparkles, Upload } from 'lucide-react';

interface Step {
  icon: React.ReactNode;
  step: string;
  title: string;
  description: React.ReactNode;
  image?: string | string[];
}

export const HowToUse: React.FC = () => {
  const { t } = useTranslation();

  const steps: Step[] = [
    {
      icon: <FileText className="w-8 h-8 text-cyan-500" />,
      step: t('steps.step1.label'),
      title: t('steps.step1.title'),
      description: (
        <Trans i18nKey="steps.step1.desc">
          Open <a href="https://notebooklm.google.com/" target="_blank" rel="noopener noreferrer" className="relative inline-block font-bold text-slate-900 before:absolute before:-bottom-1 before:left-0 before:block before:-skew-y-3 before:w-full before:h-3 before:bg-cyan-200/60 before:-z-10 hover:before:bg-cyan-300/80 transition-colors px-1">NotebookLM</a> and create a new notebook with your sources.
        </Trans>
      ),
      image: [
        "/slide-deck-guide-step1-homepage.png",
        "/slide-deck-guide-step1-2-homepage.png"
      ]
    },
    {
      icon: <Sparkles className="w-8 h-8 text-purple-500" />,
      step: t('steps.step2.label'),
      title: t('steps.step2.title'),
      description: t('steps.step2.desc'),
      image: "/slide-deck-guide-homepage.png"
    },
    {
      icon: <Upload className="w-8 h-8 text-green-500" />,
      step: t('steps.step3.label'),
      title: t('steps.step3.title'),
      description: t('steps.step3.desc'),
      image: "/slide-deck-guide-step3-homepage.png"
    }
  ];

  return (
    <div className="w-full max-w-5xl mx-auto mt-24 px-6">
      <div className="text-center mb-12">
        <h2 className="text-3xl font-bold font-display text-slate-900 mb-4">{t('steps.title')}</h2>
        <p className="text-slate-500">{t('steps.subtitle')}</p>
      </div>

      <div className="grid md:grid-cols-3 gap-8">
        {steps.map((item, index) => (
          <div key={index} className="relative group">
            {/* Connecting Line (Desktop only) */}
            {index < steps.length - 1 && (
              <div className="hidden md:block absolute top-12 left-1/2 w-full h-[2px] bg-gradient-to-r from-slate-200 to-transparent z-0" />
            )}
            
            <div className="relative z-10 bg-white p-8 rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow duration-300 h-full flex flex-col items-start text-left">
              <div className="w-16 h-16 bg-slate-50 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                {item.icon}
              </div>
              
              <div className="inline-block px-3 py-1 rounded-full bg-slate-100 text-xs font-bold text-slate-500 mb-3">
                {item.step}
              </div>
              
              <h3 className="text-xl font-bold text-slate-800 mb-3">{item.title}</h3>
              <p className="text-slate-500 leading-relaxed mb-4">{item.description}</p>
              
              {/* Optional Image(s) */}
              {item.image && (
                Array.isArray(item.image) ? (
                  item.image.map((imgSrc, imgIndex) => (
                    <div key={imgIndex} className="mt-2 rounded-lg overflow-hidden border border-slate-100 shadow-sm w-full">
                      <img 
                        src={imgSrc} 
                        alt={`${item.title} ${imgIndex + 1}`} 
                        className="w-full h-auto object-cover hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  ))
                ) : (
                  <div className="mt-2 rounded-lg overflow-hidden border border-slate-100 shadow-sm w-full">
                    <img 
                      src={item.image} 
                      alt={item.title} 
                      className="w-full h-auto object-cover hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                )
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

